import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PeopleSearchEngine from "../peopleSearchEngine/peopleSearchEngine";
import ProfileCard from "../profileCard/profileCard";
import InvitePopup from "../invitePopup/invitePopup";

const ExplorePeoplePage = ({ user }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isInviting, setIsInviting] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [inviteTarget, setInviteTarget] = useState(null);
  const [showInvitePopup, setShowInvitePopup] = useState(false);
  const [analyzedSummaries, setAnalyzedSummaries] = useState({}); 

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const handleInvite = async (receiver, projectId, roleId, roleTitle) => {
  if (!user?.email) {
    alert("You must be logged in to send an invite.");
    return;
  }

  if (!receiver?.email || !projectId) {
    console.error("❌ Invite failed: Missing recipient email or project ID.");
    return;
  }

  setIsInviting(true); // // START loading

  const receiverEmail = receiver.email;
  const displayName = receiver.displayName || receiverEmail.split("@")[0];
  const recipientFirstName = displayName.split(" ")[0];

  console.log("📨 Sending invite...");
  console.log("👤 Sender:", user.email);
  console.log("🎯 Recipient Email:", receiverEmail);
  console.log("🧍‍♂️ Display Name:", displayName);
  console.log("🧍‍♂️ First Name:", recipientFirstName);
  console.log("📌 Project ID:", projectId);
  console.log("🎭 Role Title:", roleTitle);

  try {
    // Step 1: Get Codi's Insight about sender
    const insightRes = await fetch(`${BACKEND_URL}/codi/analyze/user`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        targetEmail: user.email,
        requesterEmail: receiverEmail,
      }),
    });

    const insightData = await insightRes.json();
    const codiInsight =
      insightData.summary && insightData.summary.trim().length > 10
        ? insightData.summary.trim()
        : "Codi couldn’t find much — maybe this builder’s just mysterious ✨";

    console.log("🧠 Codi Insight:", codiInsight);

    // Step 2: Create invite in DB
    const createRes = await fetch(`${BACKEND_URL}/invites/create`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        senderEmail: user.email,
        recipientEmail: receiverEmail,
        projectId,
        roleId: roleId || null,
        roleTitle: roleTitle || null,
        codiInsight,
      }),
    });

    const createData = await createRes.json();
    if (!createRes.ok) throw new Error(createData.message || "Failed to create invite");

    console.log("✅ Invite created in DB");

    // Step 3: Send invite email
    const emailRes = await fetch(`${BACKEND_URL}/codi/invite/emailman`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        senderEmail: user.email,
        senderName: user.name || `${user.firstName} ${user.lastName}`,
        recipientEmail: receiverEmail,
        recipientName: recipientFirstName,
        projectId,
        roleTitle: roleTitle || null,
        codiInsight,
      }),
    });

    const emailData = await emailRes.json();
    if (!emailRes.ok) throw new Error(emailData.message || "Failed to send email");

    console.log("📧 Email sent:", emailData);
    alert("✅ Invite sent successfully!");
  } catch (err) {
    console.error("❌ Invite error:", err.message);
    alert("Failed to send invite.");
  }finally {
    setIsInviting(false); // END loading
  }

};


  const handleAnalyzeBuilder = async (email) => {
  if (!email || !user?.email) return;
  if (analyzedSummaries[email]) return; // Skip if already analyzed

  try {
    const res = await fetch(`${BACKEND_URL}/codi/analyze/user`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        targetEmail: email,
        requesterEmail: user.email,
      }),
    });

    const data = await res.json();
    console.log("🧠 Summary from backend:", data.summary);

    const cleanSummary =
      data.summary && data.summary.trim().length > 10
        ? data.summary.trim()
        : "Codi couldn’t find much — maybe this builder’s just mysterious ✨";

    setAnalyzedSummaries((prev) => ({
      ...prev,
      [email]: cleanSummary,
    }));
  } catch (err) {
    console.error("❌ Failed to analyze builder:", err.message);
    setAnalyzedSummaries((prev) => ({
      ...prev,
      [email]: "Something went wrong. Try again later.",
    }));
  }
};

  const handleProfileClick = (email) => {
    if (!email) return;
    const username = email.split("@")[0];
    navigate(`/${username}`);
  };

  useEffect(() => {
    const fetchProfiles = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${BACKEND_URL}/profiles/get-profiles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        const users = data.users || [];
        const allEmails = users.map((u) => u.email.toLowerCase());
        const openRoleRes = await fetch(`${BACKEND_URL}/profiles/get-openRoles`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ emails: allEmails }),
        });
        const openRolesData = await openRoleRes.json();
        const rolesMap = openRolesData.rolesMap || {};
        const enriched = users.map((u) => ({
          ...u,
          openRoles: rolesMap[u.email.toLowerCase()] || [],
        }));
        setProfiles(enriched);
      } catch (err) {
        console.error("❌ Fetch error:", err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProfiles();
  }, [token]);

  return (
    <div className="w-full flex flex-col items-center px-4">
      <div className="w-full max-w-[1008px] flex flex-col gap-10">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full gap-4">
          <div className="text-black text-lg sm:text-xl font-medium tracking-tight whitespace-nowrap">
            Browse Profiles
          </div>
          <div className="w-full sm:w-80">
            <PeopleSearchEngine searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          </div>
        </div>

        {loading ? (
          <div className="text-gray-400">Loading profiles...</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {profiles
              .filter((p) => p.email !== user?.email)
              .filter((p) => {
                const term = searchTerm.toLowerCase();
                const name = p.displayName?.toLowerCase() || "";
                const identity = (p.identity || "").toLowerCase();
                const skills = (p.skills || []).join(", ").toLowerCase();
                return name.includes(term) || identity.includes(term) || skills.includes(term);
              })
              .map((profile, i) => (
                <ProfileCard
                  key={profile.id || i}
                  profile={profile}
                  user={user}
                  onProfileClick={handleProfileClick}
                  onAnalyze={handleAnalyzeBuilder}
                  onInvite={(profile) => {
                    fetch(`${BACKEND_URL}/projects/user/${user.email}`, {
                      headers: { Authorization: `Bearer ${token}` },
                    })
                      .then((res) => res.json())
                      .then((data) => {
                        const ownedProjects = data.projects || [];
                        if (ownedProjects.length === 0) {
                          setInviteTarget({
                            name: profile.displayName || "Builder",
                            email: profile.email,
                            showCreatePrompt: true,
                          });
                        } else {
                          setInviteTarget({
                            name: profile.displayName || "Builder",
                            email: profile.email,
                            showCreatePrompt: false,
                          });
                        }
                        setShowInvitePopup(true);
                      })
                      .catch((err) => {
                        console.error("❌ Error checking projects:", err.message);
                      });
                  }}
                  summary={analyzedSummaries[profile.email] || ""}
                />
              ))}
          </div>
        )}

        {showInvitePopup && inviteTarget && (
          <InvitePopup
            receiverName={inviteTarget.name}
            receiverEmail={inviteTarget.email}
            userEmail={user?.email}
            showCreatePrompt={inviteTarget.showCreatePrompt}
            onClose={() => setShowInvitePopup(false)}
            onConfirm={(projectId, roleId) => {
              handleInvite(
                {
                  email: inviteTarget.email,
                  displayName: inviteTarget.name,
                },
                projectId,
                roleId
              );
              setShowInvitePopup(false);
            }}
          />
        )}
      </div>

      {isInviting && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="bg-white px-6 py-4 rounded-lg shadow-lg text-center">
            <p className="text-gray-700 text-lg font-medium">Sending invite...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExplorePeoplePage;
